import { Children, cloneElement, Fragment, isValidElement } from "react";

import type { ReactNode } from "react";

// License of original implementation of `flattenChildren`
// from https://github.com/grrowl/react-keyed-flatten-children/commit/0b4fd6dad491905e73551c9d4496198f6b87eb41
//
// Copyright (c) 2019 Tom McKenzie <tom@chillidonut.com>
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

/**
 * Faster grrowl/react-keyed-flatten-children, refactored to remove array allocation as much as possible.
 *
 * SS 2021-09-17
 * The performance win from this implementation on my laptop was
 * like 2-4 ms in SpacingGroup.stories 25x25 benchmark
 * where it's around 40 ms to render when using grrowl/react-keyed-flatten-children.
 */
const flattenChildrenImpl = (out: (React.JSX.Element | number | string)[], children: ReactNode, keys: string) => {
	const childrenArray = Children.toArray(children);
	const length = childrenArray.length;

	for (let nodeIndex = 0; nodeIndex < length; nodeIndex++) {
		const node = childrenArray[nodeIndex];

		if (isValidElement(node) && node.type === Fragment) {
			flattenChildrenImpl(out, (node.props as { children?: ReactNode }).children, keys + "." + (node.key || nodeIndex));
		} else {
			if (isValidElement(node)) {
				out.push(
					cloneElement(node, {
						key: keys + "." + node.key,
					}),
				);
			} else if (typeof node === "string" || typeof node === "number") {
				out.push(node);
			}
		}
	}
};

export const flattenChildren = (children: ReactNode) => {
	const out: (React.JSX.Element | number | string)[] = [];

	flattenChildrenImpl(out, children, "");

	return out;
};
